import { StepProps } from '@components/connectors/types'
import { ExternalLink } from '@components/ExternalLink'
import { fetchVenuesForSevenroomsAudience } from '@services/venue/integrations/SevenroomsAudienceService'
import {
  getSevenroomsVenueIntegration,
  saveSevenroomsVenueIntegration,
} from '@services/venue/VenueIntegrationService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getSevenroomsAudiences,
  selectSevenroomsAudienceById,
  selectSevenroomsAudiences,
} from '@store/venue/integrations/SevenroomsAudienceReducer'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { ContentWrapper } from './ContentWrapper'

const { Text } = Typography
export const APIKeySelection = ({ venue, product, onNext }: StepProps) => {
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const audienceId = Form.useWatch('audience', form)

  const audiences = useAppSelector(selectSevenroomsAudiences())
  const selectedAudience = useAppSelector(
    selectSevenroomsAudienceById(audienceId)
  )
  const { isLoading } = useAppSelector((state) => state.sevenroomsAudience)

  const [venues, setVenues] = useState<{ id: string; name: string }[]>([])
  const [venuesAreLoading, setVenuesAreLoading] = useState<boolean>(false)

  const handleNextClicked = async () => {
    try {
      const values = await form.validateFields()
      await saveSevenroomsVenueIntegration(venue.slug, product, values)
      onNext && onNext()
    } catch (error) {
      //TODO: Handle error
    }
  }

  useEffect(() => {
    if (!venue) return
    dispatch(getSevenroomsAudiences(initialQuery))
    getSevenroomsVenueIntegration(venue.slug, product).then((result) => {
      form.setFieldsValue({
        audience: result.data.integrableId,
        venueProviderId: result.data.venueProviderId,
      })
    })
  }, [venue])

  useEffect(() => {
    if (!selectedAudience || !selectedAudience.isGlobal) {
      return
    }
    setVenuesAreLoading(true)
    fetchVenuesForSevenroomsAudience(selectedAudience.id)
      .then((result) => {
        setVenues(result.data.venues)
      })
      .catch(() => {
        //TODO: Handle error
      })
      .finally(() => {
        setVenuesAreLoading(false)
      })
  }, [selectedAudience])

  return (
    <>
      <ContentWrapper>
        <Form form={form} layout="vertical">
          <Form.Item
            name={'audience'}
            label={
              <Space orientation="horizontal" size={5}>
                <Text>
                  Select an audience from the ones you created on the{' '}
                  <ExternalLink
                    content={'Sevenrooms audiences page.'}
                    to={'/settings/integrations/sevenrooms-audiences'}
                  />
                </Text>
              </Space>
            }
          >
            <Select
              loading={isLoading}
              options={[
                {
                  label: <span>Global</span>,
                  title: 'global',
                  options: audiences
                    .filter((a) => a.isGlobal)
                    .map((a) => ({
                      value: a.id,
                      label: (
                        <Space size={0}>
                          {a.isDefault && <Tag color="red">Default</Tag>}
                          {a.name}
                        </Space>
                      ),
                    })),
                },
                {
                  label: <span>Venue level</span>,
                  title: 'venue',
                  options: audiences
                    .filter((a) => !a.isGlobal)
                    .map((a) => ({
                      value: a.id,
                      label: (
                        <Space size={0}>
                          {a.name}
                          {a.isDefault && <Tag color="red">Default</Tag>}
                        </Space>
                      ),
                    })),
                },
              ]}
            />
          </Form.Item>
          {selectedAudience && !selectedAudience.isGlobal && (
            <Alert
              style={{ marginBottom: 16 }}
              title={`You have selected a venue-level API key. The next step is to input the Venue ID. This information is available on the document Sevenrooms sent you after the API Key / audience creation`}
              type="info"
            />
          )}
          {selectedAudience && (
            <>
              {selectedAudience.isGlobal && (
                <Form.Item label={'Venue'} name={'venueProviderId'}>
                  <Select
                    loading={venuesAreLoading}
                    options={venues.map((venue) => ({
                      value: venue.id,
                      label: venue.name,
                    }))}
                  />
                </Form.Item>
              )}
              {!selectedAudience.isGlobal && (
                <Form.Item label={'Venue ID'} name={'venueProviderId'}>
                  <Input />
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </ContentWrapper>
      <Flex justify="flex-end" gap={8}>
        <Button type="primary" loading={false} onClick={handleNextClicked}>
          Next
        </Button>
      </Flex>
    </>
  )
}
